import React, { Fragment } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Drawer,
  Divider,
  Chip,
} from "@mui/material";
import { toast } from "react-toastify";
import { supplierService } from "../../services/supplier.service";
import { SupplierFormDialog } from "./SupplierFormDialog";
import { DeleteConfirmationDialog } from "../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import {
  IESDoc,
  TableColumn,
  WithId,
  FilterGroup,
  QuickFilter,
} from "../../../../app.interface";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { AppTable } from "../../../../components/AppTable";
import { AppSearch } from "../../../../components/AppSearch";
import { PageHelper } from "../../components/PageHelper/PageHelper";
import { hasPermission } from "../../../auth/utils/permission.utils";
import { useAuth } from "../../../auth/hooks/useAuth";
interface SupplierContact {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
}

interface SupplierFormData {
  name: string;
  website_url?: string;
  address?: string;
  notes?: string;
  contacts: SupplierContact[];
}

export const Suppliers: React.FC = () => {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = React.useState<IESDoc[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedSupplier, setSelectedSupplier] = React.useState<IESDoc | null>(
    null
  );
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [sortField, setSortField] = React.useState<string>("name.keyword");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [selectedSupplierDetails, setSelectedSupplierDetails] =
    React.useState<IESDoc | null>(null);

  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter suppliers by their current status",
      options: [
        {
          id: "active",
          label: "Active",
          value: "active",
          icon: "check_circle",
          color: "#059669",
          description: "Currently active suppliers",
        },
        {
          id: "inactive",
          label: "Inactive",
          value: "inactive",
          icon: "cancel",
          color: "#DC2626",
          description: "Inactive suppliers",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort suppliers by field",
      options: [
        {
          id: "name_asc",
          label: "Name (A-Z)",
          value: "name_asc",
          icon: "arrow_upward",
        },
        {
          id: "name_desc",
          label: "Name (Z-A)",
          value: "name_desc",
          icon: "arrow_downward",
        },
        {
          id: "created_asc",
          label: "Created (Oldest)",
          value: "created_asc",
          icon: "schedule",
        },
        {
          id: "created_desc",
          label: "Created (Newest)",
          value: "created_desc",
          icon: "update",
        },
      ],
    },
  ];

  const MasterColumns: TableColumn[] = [
    {
      id: "supplierId",
      header: "Supplier ID",
      field: "supplierId",
      type: "action",
      width: "180px",
      renderAction: (row) => {
        return (
          <Fragment>
            <Typography fontWeight={600}>
              {`S-${row.supplierId.substring(0, 9).toUpperCase()}`}
            </Typography>
          </Fragment>
        );
      },
    },
    {
      id: "name",
      header: "Supplier Name",
      field: "name",
      filterField: "name.keyword",
      type: "text",
      sortable: true,
      width: "200px",
    },
    {
      id: "contact",
      header: "Contact Information",
      field: "contact",
      type: "contact",
      width: "300px",
      contactName: "contactName",
      contactEmail: "contactEmail",
    },
    {
      id: "address",
      header: "Address",
      field: "address",
      type: "text",
      width: "300px",
    },
    {
      id: "status",
      header: "Status",
      field: "status",
      filterField: "is_deleted",
      type: "status",
      width: "120px",
      sortable: true,
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "100px",
      renderAction: (row: WithId & Record<string, any>) => (
        <div className="flex gap-2 justify-end">
          <Tooltip title="Edit Supplier" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const originalSupplier = suppliers.find(
                  (s) => s._id === row._id
                );
                handleOpenFormDialog(originalSupplier);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#0891B2", 0.08),
                color: "#0891B2",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: (theme) => alpha("#0891B2", 0.12),
                  boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>edit_outlined</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Supplier" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(row);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#DC2626", 0.08),
                color: "#DC2626",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: (theme) => alpha("#DC2626", 0.12),
                  boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  React.useEffect(() => {
    loadSuppliers();
  }, [page, pageSize, sortField, sortOrder, search, filters]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const response = await supplierService.listSuppliers({
        searchQuery: search,
        sortField,
        sortOrder,
        pageIndex: page,
        pageSize,
        ...filters,
      });
      setSuppliers(response.data);
      setTotalItems(response.metadata.total.value);
    } catch (error) {
      toast.error("Failed to load suppliers");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSupplier = async (data: SupplierFormData) => {
    try {
      setSaving(true);
      if (selectedSupplier) {
        await supplierService.updateSupplier({
          ...data,
          _id: selectedSupplier._id,
        });
        toast.success("Supplier updated successfully");
      } else {
        await supplierService.createSupplier(data);
        toast.success("Supplier created successfully");
      }
      handleCloseFormDialog();
      loadSuppliers();
    } catch (error) {
      toast.error("Error saving supplier");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSupplier = async () => {
    if (!selectedSupplier) return;
    try {
      setSaving(true);
      await supplierService.deleteSupplier(selectedSupplier._id);
      toast.success("Supplier deleted successfully");
      handleCloseDeleteDialog();
      loadSuppliers();
    } catch (error) {
      toast.error("Failed to delete supplier");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFormDialog = (supplier?: IESDoc) => {
    setSelectedSupplier(supplier || null);
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setSelectedSupplier(null);
    setFormDialogOpen(false);
  };

  const handleOpenDeleteDialog = (supplier: IESDoc) => {
    setSelectedSupplier(supplier);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedSupplier(null);
    setDeleteDialogOpen(false);
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleRowClick = (row: WithId & Record<string, any>) => {
    const supplier = suppliers.find((s) => s._id === row._id);
    if (supplier) {
      setSelectedSupplierDetails(supplier);
    }
  };

  const handleDrawerClose = () => {
    setSelectedSupplierDetails(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Supplier Management"
        subtitle="Manage your organization's suppliers"
        icon="business"
        actions={
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<Icon>add</Icon>}
              onClick={() => handleOpenFormDialog()}
              disabled={
                !hasPermission(
                  "inventory.suppliers.create",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                bgcolor: "#0891B2",
                "&:hover": {
                  bgcolor: "#0E7490",
                },
              }}
            >
              Add Supplier
            </Button>
          </Box>
        }
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppSearch
          placeholder="Search suppliers by name or address..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppTable
          data={suppliers.map((supplier) => ({
            _id: supplier._id,
            name: supplier.name,
            address: supplier.address,
            supplierId: supplier.supplier_id || supplier._id,
            createdAt: supplier.created_at,
            createdBy: supplier.created_by_email,
            status: supplier.is_deleted ? "inactive" : "active",
            contactName: supplier.contacts?.[0]?.name || supplier.name,
            contactEmail: supplier.contacts?.[0]?.email || "N/A",
            website: supplier.website_url,
            notes: supplier.notes,
          }))}
          allowHeaderWrap
          columns={MasterColumns}
          variant="default"
          sortConfig={{ field: sortField, direction: sortOrder }}
          onSort={handleSort}
          loading={loading}
          header={{
            title: "Suppliers List",
            subtitle: "Your current list of suppliers",
          }}
          selectable
          onSelectionChange={(selected) => {
            console.log("Selected:", selected);
          }}
          styleOptions={{
            bordered: true,
            columnSeparators: true,
            headerBackground: alpha("#0891B2", 0.02),
            rowHoverBackground: alpha("#0891B2", 0.04),
            borderColor: alpha("#0891B2", 0.1),
          }}
          toolbarActions={{
            refresh: {
              label: "Refresh List",
              is_disabled: loading,
              onClick: () => loadSuppliers(),
            },
            export: {
              label: "Export Suppliers",
              is_disabled: loading,
              options: {
                formats: ["csv", "excel", "json"],
                filename: "suppliers-list",
              },
            },
          }}
          pagination={{
            total: totalItems,
            page: page,
            pageSize: pageSize,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          onRowClick={handleRowClick}
          rowProps={{
            sx: {
              cursor: "pointer",
              "&:hover": {
                bgcolor: alpha("#0891B2", 0.04),
              },
            },
          }}
        />
      </Paper>

      {/* Supplier Details Drawer */}
      <Drawer
        anchor="right"
        open={!!selectedSupplierDetails}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: "100%",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
            borderLeft: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          },
        }}
      >
        {selectedSupplierDetails ? (
          <Fragment>
            <Box
              sx={{
                px: 3,
                py: 2,
                background: "#0891B2",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Icon>business</Icon>
                <Typography variant="subtitle1" fontWeight={600}>
                  Supplier Details
                </Typography>
              </Box>
              <IconButton
                onClick={handleDrawerClose}
                size="small"
                sx={{
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <Icon>close</Icon>
              </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
              <Box display="grid" gap={3}>
                <DetailItem
                  icon="business"
                  label="Supplier Name"
                  value={selectedSupplierDetails.name || "N/A"}
                />
                <DetailItem
                  icon="location_on"
                  label="Address"
                  value={selectedSupplierDetails.address || "N/A"}
                />
                {selectedSupplierDetails.website_url && (
                  <DetailItem
                    icon="language"
                    label="Website"
                    value={selectedSupplierDetails.website_url}
                    isLink
                  />
                )}
                <DetailItem
                  icon="fiber_manual_record"
                  label="Status"
                  value={
                    selectedSupplierDetails.is_deleted ? "Inactive" : "Active"
                  }
                  valueColor={
                    selectedSupplierDetails.is_deleted ? "#DC2626" : "#059669"
                  }
                />
                <DetailItem
                  icon="schedule"
                  label="Created At"
                  value={
                    selectedSupplierDetails.created_at
                      ? new Date(
                          selectedSupplierDetails.created_at
                        ).toLocaleDateString()
                      : "N/A"
                  }
                />
                <DetailItem
                  icon="person"
                  label="Created By"
                  value={selectedSupplierDetails.created_by_email || "N/A"}
                />
              </Box>

              {selectedSupplierDetails.contacts &&
                selectedSupplierDetails.contacts.length > 0 && (
                  <>
                    <Divider
                      sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }}
                    />

                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        color: "text.primary",
                        fontFamily: "Montserrat",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }}>contacts</Icon>
                      Contact Information
                    </Typography>

                    <Box display="grid" gap={3}>
                      {selectedSupplierDetails.contacts.map(
                        (contact: SupplierContact, index: number) => (
                          <Box
                            key={index}
                            sx={{
                              p: 2,
                              borderRadius: 1,
                              bgcolor: alpha("#0891B2", 0.04),
                              border: "1px solid",
                              borderColor: alpha("#0891B2", 0.1),
                            }}
                          >
                            <DetailItem
                              icon="person"
                              label="Contact Name"
                              value={contact.name || "N/A"}
                            />
                            <Box sx={{ mt: 2 }} display="grid" gap={1.5}>
                              {contact.email && (
                                <Box display="flex" gap={1} alignItems="center">
                                  <Icon
                                    sx={{
                                      fontSize: 18,
                                      color: "text.secondary",
                                    }}
                                  >
                                    email
                                  </Icon>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#0891B2",
                                      fontFamily: "Montserrat",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                    component="a"
                                    href={`mailto:${contact.email}`}
                                  >
                                    {contact.email}
                                  </Typography>
                                </Box>
                              )}
                              {contact.phone && (
                                <Box display="flex" gap={1} alignItems="center">
                                  <Icon
                                    sx={{
                                      fontSize: 18,
                                      color: "text.secondary",
                                    }}
                                  >
                                    phone
                                  </Icon>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#0891B2",
                                      fontFamily: "Montserrat",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                    component="a"
                                    href={`tel:${contact.phone}`}
                                  >
                                    {contact.phone}
                                  </Typography>
                                </Box>
                              )}
                              {contact.whatsapp && (
                                <Box display="flex" gap={1} alignItems="center">
                                  <Icon
                                    sx={{
                                      fontSize: 18,
                                      color: "text.secondary",
                                    }}
                                  >
                                    whatsapp
                                  </Icon>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#0891B2",
                                      fontFamily: "Montserrat",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                    component="a"
                                    href={`https://wa.me/${contact.whatsapp}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {contact.whatsapp}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )
                      )}
                    </Box>
                  </>
                )}

              {selectedSupplierDetails.notes && (
                <>
                  <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      color: "text.primary",
                      fontFamily: "Montserrat",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }}>note</Icon>
                    Notes
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontFamily: "Montserrat",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {selectedSupplierDetails.notes}
                  </Typography>
                </>
              )}

              <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />

              <Box display="flex" gap={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Icon>edit</Icon>}
                  onClick={() => {
                    handleOpenFormDialog(selectedSupplierDetails);
                    handleDrawerClose();
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.suppliers.edit",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{
                    borderColor: "#0891B2",
                    color: "#0891B2",
                    "&:hover": {
                      borderColor: "#0E7490",
                      bgcolor: alpha("#0891B2", 0.04),
                    },
                  }}
                >
                  Edit Supplier
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<Icon>delete</Icon>}
                  onClick={() => {
                    handleOpenDeleteDialog(selectedSupplierDetails);
                    handleDrawerClose();
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.suppliers.delete",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{
                    borderColor: "#DC2626",
                    color: "#DC2626",
                    "&:hover": {
                      borderColor: "#B91C1C",
                      bgcolor: alpha("#DC2626", 0.04),
                    },
                  }}
                >
                  Delete Supplier
                </Button>
              </Box>
            </Box>
          </Fragment>
        ) : null}
      </Drawer>

      <SupplierFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        onSave={handleSaveSupplier}
        supplier={
          selectedSupplier
            ? {
                name: selectedSupplier.name,
                website_url: selectedSupplier.website_url,
                address: selectedSupplier.address,
                notes: selectedSupplier.notes,
                contacts: selectedSupplier.contacts || [],
              }
            : null
        }
        loading={saving}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteSupplier}
        title="Delete Supplier"
        itemName={selectedSupplier?.name || ""}
        itemDescription={selectedSupplier?.address || ""}
        loading={saving}
        icon="business"
      />
    </Box>
  );
};

const DetailItem: React.FC<{
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
  isLink?: boolean;
}> = ({ icon, label, value, valueColor, isLink }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 0.5,
        fontFamily: "Montserrat",
      }}
    >
      <Icon sx={{ fontSize: 16 }}>{icon}</Icon>
      {label}
    </Typography>
    {isLink ? (
      <Typography
        variant="body1"
        component="a"
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          fontWeight: 500,
          color: "#0891B2",
          fontFamily: "Montserrat",
          textDecoration: "none",
          display: "block",
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        {value}
      </Typography>
    ) : (
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          color: valueColor || "text.primary",
          fontFamily: "Montserrat",
        }}
      >
        {value}
      </Typography>
    )}
  </Box>
);
